import { createFileRoute } from '@tanstack/react-router';
import { PLANS, formatPrice } from '@goodbuild/shared';
import { LegalDoc, type LegalSection } from '../components/legal/LegalDoc.js';
import { LEGAL } from '../lib/legal.js';

export const Route = createFileRoute('/terms')({ component: Terms });

const MONTHLY = formatPrice(PLANS.supporter_month.priceCents, PLANS.supporter_month.currency);

/**
 * The terms of service.
 *
 * Written to be read rather than survived: short clauses, ordinary words, and the two things a
 * membership to a studio making unfinished games has to be honest about — that a build can
 * break and that a game may never be finished — said in their own sections rather than buried.
 *
 * The data section is short because there is little to say: nothing in the app tracks anybody.
 *
 * The prices come from the plan table rather than being typed in here, so a price change cannot
 * leave the terms quoting a number the checkout no longer charges.
 */
const SECTIONS: LegalSection[] = [
  {
    id: 'who-we-are',
    heading: 'Who you are contracting with',
    body: [
      `${LEGAL.brand} is operated by ${LEGAL.entity}, a sole proprietorship run by ${LEGAL.proprietor} and registered in ${LEGAL.register}.`,
      `Registered address: ${LEGAL.address}. NIP (tax identification number): ${LEGAL.nip}. REGON (statistical number): ${LEGAL.regon}.`,
      `You can reach us at ${LEGAL.contactEmail}. In these terms, "we", "us" and "our" mean that business, and "you" means the person using ${LEGAL.brand}.`,
      'These terms apply to the GOOD BUILD web app, the Windows and Linux desktop apps, and the Android app. Using any of them means you accept them.',
    ],
  },
  {
    id: 'what-this-is',
    heading: 'What GOOD BUILD is',
    body: [
      'GOOD BUILD is a membership. It gives you access to the games we are making while we are making them, and to each new build as we push it.',
      'It is not a shop. You are not buying a game, a licence key or a copy of anything. You are paying for access to work in progress, for as long as your membership runs.',
      'Browsing is free. Anyone can look at the catalogue, the studio page and the prices without an account. A membership is what turns the Play button on.',
    ],
  },
  {
    id: 'your-account',
    heading: 'Your account',
    body: [
      'Signing in is handled by Discord. You need a Discord account, and Discord’s own terms apply to that account alongside ours.',
      'An account is for one person. Keep your sign in details to yourself: anything done with your account is treated as done by you. Tell us at once if you think somebody else has got into it.',
      'You must be old enough to enter into a contract where you live, and at least 16. If you are under 18, you need your parent or guardian to agree to these terms.',
      'You can sign out of every device at once from your account screen, and you can delete your account there too.',
    ],
  },
  {
    id: 'membership',
    heading: 'Membership and what it opens',
    body: ['While your membership is valid you can:'],
    list: [
      'install and play every game and prototype we have published, on every platform we build for;',
      'download each new build as we push it, including daily builds;',
      'claim the supporter role on our Discord, which opens the private channels.',
    ],
    after: [
      'Access ends when your membership ends. Games installed on your machine stop being playable through the app, because the app checks your access before it launches anything.',
      'We may add, change or remove what a membership includes. If a change takes away something substantial you were paying for, we will tell you before it happens and you can cancel.',
    ],
  },
  {
    id: 'prices',
    heading: 'Prices, VAT and payment',
    body: [
      `Prices are shown in euro and include VAT. A supporter membership is ${MONTHLY} a month at the time of writing; the current price for every plan is always on the membership screen.`,
      'Nothing is added at checkout: the price on the membership screen is the total you will be charged, whichever country you are in. Any discount code you enter is applied before you confirm.',
      'Payments are taken by Creem (Creem B.V.), not by us. Creem is the merchant of record for every membership: they sell it to you, they charge you, they handle VAT and they issue your receipt, and their terms and privacy notice apply to the payment itself. Your card details go to Creem and never to us — we do not see them, and we could not store them if we wanted to.',
      'Creem will show up on your bank or card statement rather than GOOD BUILD, which is normal for a merchant of record and is not a charge from somebody else.',
      'A membership renews automatically at the end of each period until you cancel. We will tell you at least 30 days before any price increase takes effect, and you can cancel before it does.',
      'If a payment fails, we may retry it and we may suspend access until it goes through.',
    ],
  },
  {
    id: 'withdrawal',
    heading: 'Your 14 day right to withdraw, and the waiver',
    body: [
      'As a consumer in the EU you normally have 14 days to withdraw from a contract for digital content without giving a reason.',
      'Access to GOOD BUILD starts immediately, which is the point of it. So before we create a checkout we ask you to tick a box confirming two things: that you want access to start right away, and that you understand this means you lose the 14 day right to withdraw once it has started. Without that box ticked, no checkout is created.',
      'If you do not want to give up the withdrawal right, do not tick the box. You can still browse everything that is free.',
      `If you ticked the box by mistake and have not used your access, write to ${LEGAL.contactEmail} and we will sort it out.`,
    ],
  },
  {
    id: 'cancelling',
    heading: 'Cancelling and refunds',
    body: [
      'You can cancel at any time from your account screen. Cancelling stops the next payment; it does not end the period you have already paid for.',
      'You keep access until the end of the current period. We do not refund the unused part of a period you chose to cancel, having asked for access to start immediately.',
      'If you change to a different plan, the change takes effect the same day: we cancel the old plan and refund the days you had not used on it.',
      'None of this affects your statutory rights. If we are in breach — for example the service is unavailable for a long stretch — you are entitled to a price reduction or a refund under consumer law, and you should write to us.',
    ],
  },
  {
    id: 'licence',
    heading: 'What you may and may not do with the builds',
    body: [
      'While your membership is valid we grant you a personal, non-exclusive, non-transferable, revocable licence to install and play our builds for your own private entertainment. Everything else stays ours.',
      'You may not:',
    ],
    list: [
      'copy, share, upload, sell or otherwise distribute a build, in whole or in part;',
      'give anybody else your account or your access pass;',
      'reverse engineer, decompile or work around any technical protection, except as far as the law says you may;',
      'use a build commercially, including in a paid stream, a paid event or a commercial product, without our written permission;',
      'use anything you get here to train a machine learning model.',
    ],
    after: [
      'Recording, streaming and video-making for a personal channel is welcome and encouraged, including monetised channels, as long as the video is your own commentary and not a way of handing the game itself to somebody else. Ask us if you are unsure.',
      'Breaking these rules ends your licence immediately, and may end your membership without a refund.',
    ],
  },
  {
    id: 'your-content',
    heading: 'Feedback you send us',
    body: [
      'There is nothing to post inside the app itself. What you send us goes through our Discord or straight to the team, and those places have their own rules.',
      'If you send us feedback or an idea, we may use it without owing you anything. That is not us claiming your work: it is so that a bug report or a suggestion in a Discord thread can be acted on without a legal question attached.',
    ],
  },
  {
    id: 'your-data',
    heading: 'Your data',
    body: [
      'We do not track you across other sites, we do not sell your data, and we use no third-party analytics service. We do measure how our own app is used — which screens get opened, which games get installed and launched, how long they run, and where players get stuck inside a game — because otherwise we are building in the dark. It carries no identifier of its own, it stays in our own Cloudflare account, and you can turn it off in Settings.',
      'What we hold beyond that is the short list a membership needs to work: your account, your sign in sessions, your membership, and anything you post. The privacy policy sets out all of it, why we hold it and how long for, and it forms part of these terms.',
      `You can delete your account, and everything that goes with it, from your account screen. For anything else about your data, write to ${LEGAL.contactEmail}.`,
    ],
  },
  {
    id: 'unfinished',
    heading: 'These are unfinished games',
    body: [
      'This is the part worth reading twice. Everything on GOOD BUILD is work in progress.',
      'A build can crash, corrupt a save, lose your progress or perform badly. A feature that exists this week can be gone next week. A game can be redesigned, renamed, paused indefinitely or abandoned, and some of them will be.',
      'We do not promise that any particular game will be finished, released, sold commercially, or made available on any particular platform or store. We do not promise that a game you like today will still be on GOOD BUILD tomorrow.',
      'Nothing you pay is tied to a specific game or a specific outcome. You are paying for access to what we are working on now, not for a future release.',
    ],
  },
  {
    id: 'availability',
    heading: 'Availability and changes to the service',
    body: [
      'We try to keep everything up and working, but we do not offer a guaranteed level of service. Maintenance, third party outages and plain failures happen.',
      'We may change how GOOD BUILD works, add things, remove things, or stop the service entirely. If we are going to stop it, we will give you reasonable notice, stop taking payments, and let any period you have already paid for run out or refund it.',
      'The apps keep themselves updated. If a version is too old to work safely with the service, the app will require an update before it will carry on.',
    ],
  },
  {
    id: 'liability',
    heading: 'Our liability',
    body: [
      'Nothing in these terms limits our liability for death or personal injury caused by our negligence, for fraud, for intentional wrongdoing, or for anything else the law does not allow us to limit. Your statutory rights as a consumer are not affected by anything here.',
      'Otherwise, and to the extent the law allows: we are not liable for lost profits, lost savings, lost data, lost game progress, or any indirect or consequential loss; and our total liability to you in any twelve month period is limited to what you paid us in that period.',
      'We are not responsible for what Discord or any other third party service does or fails to do, beyond our own responsibility for choosing them.',
    ],
  },
  {
    id: 'suspension',
    heading: 'Suspension and termination',
    body: [
      'We can suspend or close an account that breaks these terms, that is used to distribute our builds, or that is used to abuse other members.',
      'Where the breach is minor and fixable, we will tell you what the problem is and give you a chance to put it right first. Where it is serious, we may act immediately.',
      'If we close your account for a breach, you do not get a refund for the rest of the period. If we close it for any other reason, you do.',
    ],
  },
  {
    id: 'changes',
    heading: 'Changes to these terms',
    body: [
      'We may update these terms. If a change materially affects you, we will tell you in the app or by email at least 30 days before it takes effect.',
      'If you do not accept a change, cancel before it takes effect. Carrying on after that means you accept it.',
    ],
  },
  {
    id: 'law',
    heading: 'Law, and where a dispute goes',
    body: [
      `These terms are governed by the law of ${LEGAL.country}.`,
      'If you are a consumer resident in the EU, you also keep the protection of the mandatory consumer rules of the country you live in, and nothing here takes those away.',
      'The European Commission runs an online dispute resolution platform at ec.europa.eu/consumers/odr. We would much rather you wrote to us first: almost everything is quicker to fix directly.',
      'If it cannot be settled, the courts with jurisdiction are those determined by the applicable consumer protection rules.',
    ],
  },
  {
    id: 'contact',
    heading: 'Contact',
    body: [
      `${LEGAL.entity}`,
      `${LEGAL.address}`,
      `NIP ${LEGAL.nip} · REGON ${LEGAL.regon}`,
      `${LEGAL.contactEmail}`,
    ],
  },
];

function Terms() {
  return (
    <LegalDoc
      eyebrow="Legal"
      title="Terms of service."
      intro="The agreement between you and the studio: what a membership gives you, what it costs, and what happens when something goes wrong. It is written to be read."
      sections={SECTIONS}
    />
  );
}
